/**
 * First-touch attribution capture (1st-party, TTL 30d).
 *
 * Persists utm_source/medium/campaign/content/term + gclid + fbclid from the
 * first pageview into localStorage. On checkout (cartCreate) these are written
 * as Shopify cart attributes (custom attributes) so they reach the order as
 * note_attributes — fixing the "customerJourney NULL on 100% of orders" gap.
 *
 * Also rebuilds the Meta `_fbc` cookie from `fbclid` when missing, so CAPI
 * events can carry fbc/fbp for better match quality.
 */

import { getExternalId, getSessionId } from '@/lib/crm/track';

const STORAGE_KEY = 'nathof_attribution';
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const CLICK_KEYS = ['gclid', 'fbclid'] as const;
const ALL_KEYS = [...UTM_KEYS, ...CLICK_KEYS, 'landing_page'] as const;

interface AttributionData {
  capturedAt: number;
  [key: string]: string | number;
}

function readStored(): AttributionData | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AttributionData;
    if (!parsed?.capturedAt || Date.now() - parsed.capturedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Build the Meta `_fbc` cookie (fb.1.<ts>.<fbclid>) when it is not already set. */
function ensureFbcCookie(fbclid: string): void {
  if (typeof document === 'undefined') return;
  if (document.cookie.match(/(?:^|;\s*)_fbc=/)) return; // already present
  const value = `fb.1.${Date.now()}.${fbclid}`;
  const maxAge = 90 * 24 * 60 * 60; // 90 days (Meta default attribution window)
  document.cookie = `_fbc=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/**
 * Capture attribution params from the current URL. Call once on first pageview.
 * First-touch wins: existing (non-expired) data is never overwritten.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const incoming: Record<string, string> = {};
    [...UTM_KEYS, ...CLICK_KEYS].forEach((k) => {
      const v = params.get(k);
      if (v) incoming[k] = v;
    });

    // Always (re)build _fbc from a fresh fbclid, even if first-touch already stored.
    if (incoming.fbclid) ensureFbcCookie(incoming.fbclid);

    if (readStored()) return; // first-touch persists (non-expired record wins)
    if (Object.keys(incoming).length === 0) return; // nothing to persist

    // Derive utm_source/medium from a bare click id when no utm_* is present, so
    // paid traffic still carries an origin: gclid → google/cpc, fbclid → meta/paid_social.
    if (!incoming.utm_source) {
      if (incoming.gclid) {
        incoming.utm_source = 'google';
        incoming.utm_medium = incoming.utm_medium || 'cpc';
      } else if (incoming.fbclid) {
        incoming.utm_source = 'meta';
        incoming.utm_medium = incoming.utm_medium || 'paid_social';
      }
    }

    const now = Date.now();
    const data: AttributionData = {
      capturedAt: now,
      ts: now,
      landing_page: window.location.pathname,
      ...incoming,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* non-blocking */
  }
}

/** Read a browser cookie value by name (returns null when absent). */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Returns GA4 client_id / session_id (read live from the GA cookies) as cart
 * attributes, so they reach the Shopify order as note_attributes. These let the
 * server-side Measurement Protocol purchase carry the buyer's real client_id
 * (fixes source=(not set)/(direct) on conversions).
 *
 * - ga_client_id: from `_ga` (GA1.1.<cid1>.<cid2> → "<cid1>.<cid2>").
 * - ga_session_id: from `_ga_2SJF6ZCKGY` (GS1.1.<sessionId>.… → "<sessionId>").
 *
 * Missing cookies are simply skipped (never throws).
 */
function getGaCartAttributes(): Array<{ key: string; value: string }> {
  const attrs: Array<{ key: string; value: string }> = [];
  try {
    const ga = readCookie('_ga'); // GA1.1.XXXXXXXXX.YYYYYYYYY
    if (ga) {
      const parts = ga.split('.');
      if (parts.length >= 4) {
        const clientId = `${parts[2]}.${parts[3]}`;
        if (clientId.length > 1) attrs.push({ key: 'ga_client_id', value: clientId });
      }
    }
    const gaSession = readCookie('_ga_2SJF6ZCKGY'); // GS1.1.SESSIONID.… or GS2.1.s<sessionId>$...
    if (gaSession) {
      let sessionId: string | null = null;
      const gs2 = gaSession.match(/^GS\d\.\d\.s(\d+)/); // GS2.1.s<sessionId>$o...
      if (gs2) {
        sessionId = gs2[1];
      } else {
        const sParts = gaSession.split('.'); // GS1.1.<sessionId>.<n>...
        if (sParts.length >= 3 && /^\d+$/.test(sParts[2])) sessionId = sParts[2];
      }
      if (sessionId) attrs.push({ key: 'ga_session_id', value: sessionId });
    }
  } catch {
    /* non-blocking */
  }
  return attrs;
}

/**
 * Returns the stored attribution as Shopify cart attributes
 * ([{ key, value }]) to attach on cartCreate / cart updates.
 */
export function getAttributionCartAttributes(): Array<{ key: string; value: string }> {
  const attrs: Array<{ key: string; value: string }> = [];
  const data = readStored();
  if (data) {
    ALL_KEYS.forEach((k) => {
      const v = data[k];
      if (v != null && String(v).length > 0) {
        attrs.push({ key: k, value: String(v) });
      }
    });
  }
  // GA client_id / session_id are read live from cookies (not first-touch storage).
  attrs.push(...getGaCartAttributes());

  // Cookies de click-match do Meta — necessários para atribuir Purchase ao clique no anúncio.
  // _fbc é reconstruído do fbclid por ensureFbcCookie(); _fbp vem do fbevents.js (só com consent granted).
  // Passam ao pedido como note_attributes, onde order-paid-tracking os lê para o CAPI.
  const fbc = readCookie('_fbc');
  if (fbc) attrs.push({ key: '_fbc', value: fbc });
  const fbp = readCookie('_fbp');
  if (fbp) attrs.push({ key: '_fbp', value: fbp });

  // Identidade do visitante (external_id + session_id do CRM). Sem isso o
  // webhook order-paid-tracking lê null em 100% dos purchase_completed, o que
  // joga ~40% das transações em "Unassigned" no GA4. order-paid-tracking já lê
  // exatamente estas chaves (`crm_external_id` e `session_id`) e mantém o
  // fallback `shopify_order_<id>` para pedidos antigos/sem atributo.
  try {
    const externalId = getExternalId();
    if (externalId) attrs.push({ key: 'crm_external_id', value: externalId });
    const sessionId = getSessionId();
    if (sessionId) attrs.push({ key: 'session_id', value: sessionId });
  } catch {
    /* non-blocking — checkout nunca pode quebrar por causa de tracking */
  }

  return attrs;
}

/**
 * Returns the stored utm/gclid/fbclid values as a plain key-value map, to be
 * appended as query params on the headless checkout URL (in addition to the cart
 * attributes). This keeps paid-traffic attribution alive across the
 * storefront -> checkout.nathof.com.br hop so GA4 doesn't bucket it as Direct.
 * Excludes landing_page and GA cookies (those are not URL-relevant for checkout).
 */
export function getAttributionQueryParams(): Record<string, string> {
  const out: Record<string, string> = {};
  const data = readStored();
  if (!data) return out;
  [...UTM_KEYS, ...CLICK_KEYS].forEach((k) => {
    const v = data[k];
    if (v != null && String(v).length > 0) out[k] = String(v);
  });
  return out;
}
